import type { FormEvent } from 'react'
import type { Company, Segment, Stage } from '@/constants/companies'

import { useRef, useState } from 'react'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import Field from '@/components/ui/field'
import SegmentBar from '@/components/generals/segment-bar'
import FormSection from '@/components/companies/new-company/form-section'
import LogoUpload from '@/components/companies/new-company/logo-upload'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

import {
    INTERACTION_TYPES,
    OWNERS,
    SEGMENTS,
    STAGES
} from '@/constants/companies'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

type FormState = {
    logo: string | null
    name: string
    segment: Segment
    stage: Stage
    owner: string
    pipelineValue: string
    openDeals: string
    winProbability: number
    interactionDate: string
    interactionType: string
}

type CreateCompanyResponse = {
    company?: Company
    message?: string
}

function emptyForm(today: string): FormState {
    return {
        logo: null,
        name: '',
        segment: SEGMENTS[0],
        stage: STAGES[0],
        owner: OWNERS[0].name,
        pipelineValue: '',
        openDeals: '1',
        winProbability: 50,
        interactionDate: today,
        interactionType: INTERACTION_TYPES[0]
    }
}

const SAVE_FAILED = "The company couldn't be saved right now. Try again in a moment."

export default function NewCompanyDialog() {
    const today = useCrmStore(state => state.today)
    const open = useCrmStore(state => state.dialog === 'company')
    const openDialog = useCrmStore(state => state.openDialog)
    const closeDialog = useCrmStore(state => state.closeDialog)
    const addCompany = useCrmStore(state => state.addCompany)
    const [form, setForm] = useState<FormState>(() => emptyForm(today))

    function setOpen(next: boolean) {
        if (next) openDialog('company')
        else closeDialog()
    }
    const [nameError, setNameError] = useState<string | null>(null)
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const nameRef = useRef<HTMLInputElement>(null)

    function update<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm(current => ({ ...current, [key]: value }))
    }

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()

        if (submitting) return

        const name = form.name.trim()

        if (!name) {
            setNameError("Enter a company name.")
            nameRef.current?.focus()

            return
        }

        setSubmitting(true)
        setFormError(null)

        try {
            const response = await fetch('/api/companies', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name,
                    logo: form.logo,
                    segment: form.segment,
                    stage: form.stage,
                    owner: form.owner,
                    pipelineValue: form.pipelineValue,
                    openDeals: form.openDeals,
                    winProbability: form.winProbability,
                    interactionDate: form.interactionDate || today,
                    interactionType: form.interactionType
                })
            })

            const payload = await response.json().catch(() => null) as CreateCompanyResponse | null

            if (!response.ok || !payload?.company) {
                setFormError(payload?.message || SAVE_FAILED)

                return
            }

            addCompany(payload.company)
        }
        catch {
            setFormError("The company couldn't be saved. Check your connection and try again.")
        }
        finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                className='max-w-[560px]'
                onCloseAutoFocus={() => {
                    setForm(emptyForm(today))
                    setNameError(null)
                    setFormError(null)
                }}
            >
                <form onSubmit={handleSubmit} noValidate className='flex flex-col' aria-busy={submitting}>
                    <DialogHeader>
                        <DialogTitle>New Company</DialogTitle>
                        <DialogDescription>
                            Add a company to the pipeline. It appears in the list right away.
                        </DialogDescription>
                    </DialogHeader>

                    <FormSection title="Company">
                        <LogoUpload
                            value={form.logo}
                            companyName={form.name}
                            disabled={submitting}
                            onChange={logo => update('logo', logo)}
                        />

                        <Field
                            label="Company name"
                            htmlFor='company-name'
                            required
                            error={nameError ?? undefined}
                        >
                            <Input
                                ref={nameRef}
                                id='company-name'
                                value={form.name}
                                onChange={event => {
                                    update('name', event.target.value)
                                    if (nameError) setNameError(null)
                                }}
                                placeholder="Acme Inc."
                                autoComplete='off'
                                maxLength={120}
                                aria-invalid={nameError ? true : undefined}
                                aria-describedby={nameError ? 'company-name-error' : undefined}
                                className='aria-invalid:border-danger'
                                autoFocus
                            />
                        </Field>

                        <div className='grid gap-4 sm:grid-cols-2'>
                            <Field label="Segment" htmlFor='company-segment'>
                                <Select value={form.segment} onValueChange={value => update('segment', value as Segment)}>
                                    <SelectTrigger id='company-segment'>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SEGMENTS.map(segment => (
                                            <SelectItem key={segment} value={segment}>
                                                {segment}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Stage" htmlFor='company-stage'>
                                <Select value={form.stage} onValueChange={value => update('stage', value as Stage)}>
                                    <SelectTrigger id='company-stage'>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STAGES.map(stage => (
                                            <SelectItem key={stage} value={stage}>
                                                {stage}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>
                    </FormSection>

                    <FormSection title="Ownership & deal">
                        <Field label="Account owner" htmlFor='company-owner'>
                            <Select value={form.owner} onValueChange={value => update('owner', value)}>
                                <SelectTrigger id='company-owner'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {OWNERS.map(owner => (
                                        <SelectItem key={owner.name} value={owner.name}>
                                            <span className='flex items-center gap-2'>
                                                <Avatar src={owner.avatar} alt='' />
                                                {owner.name}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        <div className='grid gap-4 sm:grid-cols-2'>
                            <Field label="Pipeline value" htmlFor='company-pipeline'>
                                <div className='relative'>
                                    <span
                                        aria-hidden
                                        className='text-subtle pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[14px] leading-none'
                                    >
                                        $
                                    </span>
                                    <Input
                                        id='company-pipeline'
                                        type='number'
                                        min={0}
                                        step={1000}
                                        inputMode='numeric'
                                        value={form.pipelineValue}
                                        onChange={event => update('pipelineValue', event.target.value)}
                                        placeholder='250000'
                                        className='pl-6 tabular-nums'
                                    />
                                </div>
                            </Field>
                            <Field label="Open deals" htmlFor='company-deals'>
                                <Input
                                    id='company-deals'
                                    type='number'
                                    min={0}
                                    inputMode='numeric'
                                    value={form.openDeals}
                                    onChange={event => update('openDeals', event.target.value)}
                                    className='tabular-nums'
                                />
                            </Field>
                        </div>

                        <Field
                            label="Win probability"
                            htmlFor='company-win'
                            trailing={(
                                <span className='caption-style text-foreground tabular-nums'>
                                    {form.winProbability}%
                                </span>
                            )}
                        >
                            <div className='flex flex-col gap-3'>
                                <Slider
                                    id='company-win'
                                    aria-label="Win probability"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={[form.winProbability]}
                                    onValueChange={([value]) => update('winProbability', value)}
                                />
                                <SegmentBar
                                    percent={form.winProbability}
                                    segments={40}
                                    className='h-3 w-full border border-white/4 px-px'
                                    segmentClassName='h-2'
                                    trackClassName='bg-white/8'
                                />
                            </div>
                        </Field>
                    </FormSection>

                    <FormSection title="Last interaction">
                        <div className='grid gap-4 sm:grid-cols-2'>
                            <Field label="Date" htmlFor='company-interaction-date'>
                                <Input
                                    id='company-interaction-date'
                                    type='date'
                                    max={today}
                                    value={form.interactionDate}
                                    onChange={event => update('interactionDate', event.target.value)}
                                    className='tabular-nums'
                                />
                            </Field>
                            <Field label="Type" htmlFor='company-interaction-type'>
                                <Select value={form.interactionType} onValueChange={value => update('interactionType', value)}>
                                    <SelectTrigger id='company-interaction-type'>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {INTERACTION_TYPES.map(type => (
                                            <SelectItem key={type} value={type}>
                                                {type}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>
                    </FormSection>

                    {formError && (
                        <div role='alert' className='caption-style text-danger px-6 pb-4'>
                            {formError}
                        </div>
                    )}

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant='subtle' size='sm'>
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button variant='primary' size='sm' type='submit' disabled={submitting}>
                            <PlusIcon aria-hidden className='size-3' />
                            {submitting ? "Creating…" : "Create Company"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
